/**
 * Read a gzip answer the server sent as base64 (forms_getDataSourceOptions, `projectedDocsGz`).
 *
 * WHY: the generators answer is 1.09 MB as plain JSON and about 287 KB gzipped + base64. On a slow phone the plain
 * download outlasted the 10 s race in useDataSource, which then started a second, 10.7 MB direct read (Sentry
 * OFFICEHUB-TECHNICIAN-C / CLIENT-7A). The browser's own DecompressionStream does the inflating, so no library ships.
 */

/**
 * Most the inflated answer may be. The real generators answer is about 1.1 MB; a larger one is refused so a runaway
 * (or hostile) stream cannot fill a phone's memory. Refusing throws, and the caller falls back to the direct read.
 */
export const MAX_INFLATED_BYTES = 32 * 1024 * 1024;

/** True when this browser can inflate a gzip answer; the request only asks for one when it can. */
export function canInflateGzip(): boolean {
  return (
    typeof DecompressionStream === 'function' &&
    typeof Blob === 'function' &&
    typeof Blob.prototype.stream === 'function' &&
    typeof TextDecoder === 'function'
  );
}

/**
 * @param {string} base64 base64 of a gzip stream
 * @return {Promise<unknown>} the parsed JSON
 * @throws on anything that is not valid base64 + gzip + JSON, so the caller falls back to the direct read
 */
export async function inflateGzipBase64(base64: string): Promise<unknown> {
  if (typeof base64 !== 'string' || base64.length === 0) throw new Error('gzip answer is empty');
  const binary = atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i += 1) bytes[i] = binary.charCodeAt(i);
  const reader = new Blob([bytes]).stream().pipeThrough(new DecompressionStream('gzip')).getReader();
  const decoder = new TextDecoder();
  let text = '';
  let inflated = 0;
  for (;;) {
    const { done, value } = await reader.read();
    if (done) break;
    inflated += value.length;
    if (inflated > MAX_INFLATED_BYTES) {
      await reader.cancel();
      throw new Error(`gzip answer inflates past ${MAX_INFLATED_BYTES} bytes`);
    }
    text += decoder.decode(value, { stream: true });
  }
  return JSON.parse(text + decoder.decode());
}
